// Starter Skill Templates
// Each class starts with 2-3 pre-configured skills
// Players can customize these or create new ones from scratch

import { CustomSkill, SkillCreator, EffectStone } from './SkillCreator';
import { SkillTypeId } from './SkillTypes';
import { ElementType } from '@shared/types';

export interface SkillTemplate {
  name: string;
  nameKey: string;
  description: string;
  descriptionKey: string;
  baseType: SkillTypeId;
  sliders: {
    damage: number;
    area: number;
    speed: number;
    projectiles: number;
    range: number;
  };
}

// ========================
// ALP (Warrior) Templates
// ========================
export const ALP_TEMPLATES: SkillTemplate[] = [
  {
    name: 'Kesici Darbe',
    nameKey: 'skill_template.alp.slash.name',
    description: 'Hızlı ve dengeli bir kılıç darbesi.',
    descriptionKey: 'skill_template.alp.slash.description',
    baseType: 'slash',
    sliders: { damage: 50, area: 50, speed: 60, projectiles: 50, range: 50 },
  },
  {
    name: 'Kasırga',
    nameKey: 'skill_template.alp.spin.name',
    description: 'Etrafındaki düşmanlara dönerek saldırır.',
    descriptionKey: 'skill_template.alp.spin.description',
    baseType: 'spin',
    sliders: { damage: 40, area: 70, speed: 50, projectiles: 50, range: 50 },
  },
  {
    name: 'İleri Atılış',
    nameKey: 'skill_template.alp.thrust.name',
    description: 'Düşmana doğru hızla atılarak saldırı.',
    descriptionKey: 'skill_template.alp.thrust.description',
    baseType: 'thrust',
    sliders: { damage: 60, area: 30, speed: 70, projectiles: 50, range: 60 },
  },
];

// ========================
// KAM (Shaman) Templates
// ========================
export const KAM_TEMPLATES: SkillTemplate[] = [
  {
    name: 'Ruh Mızrağı',
    nameKey: 'skill_template.kam.projectile.name',
    description: 'Düşmana ruhsal enerji fırlatır.',
    descriptionKey: 'skill_template.kam.projectile.description',
    baseType: 'projectile',
    sliders: { damage: 55, area: 30, speed: 60, projectiles: 50, range: 60 },
  },
  {
    name: 'Ruh Patlaması',
    nameKey: 'skill_template.kam.aoe.name',
    description: 'Belirli bir alanda ruhsal hasar verir.',
    descriptionKey: 'skill_template.kam.aoe.description',
    baseType: 'aoe',
    sliders: { damage: 50, area: 65, speed: 45, projectiles: 50, range: 55 },
  },
  {
    name: 'Koruyucu Totem',
    nameKey: 'skill_template.kam.totem.name',
    description: 'Alandaki düşmanlara hasar veren totem.',
    descriptionKey: 'skill_template.kam.totem.description',
    baseType: 'totem',
    sliders: { damage: 40, area: 60, speed: 50, projectiles: 50, range: 50 },
  },
];

// ========================
// MERGEN (Archer) Templates
// ========================
export const MERGEN_TEMPLATES: SkillTemplate[] = [
  {
    name: 'Ok Atışı',
    nameKey: 'skill_template.mergen.arrow.name',
    description: 'Temel ok atışı.',
    descriptionKey: 'skill_template.mergen.arrow.description',
    baseType: 'arrow',
    sliders: { damage: 55, area: 30, speed: 65, projectiles: 50, range: 60 },
  },
  {
    name: 'Çoklu Ok',
    nameKey: 'skill_template.mergen.multishot.name',
    description: 'Birden fazla ok fırlatır.',
    descriptionKey: 'skill_template.mergen.multishot.description',
    baseType: 'multishot',
    sliders: { damage: 40, area: 55, speed: 50, projectiles: 70, range: 50 },
  },
  {
    name: 'Ok Yağmuru',
    nameKey: 'skill_template.mergen.rain.name',
    description: 'Geniş bir alana ok yağdırır.',
    descriptionKey: 'skill_template.mergen.rain.description',
    baseType: 'rain',
    sliders: { damage: 35, area: 75, speed: 40, projectiles: 80, range: 55 },
  },
];

// ========================
// BORU (Wolf) Templates
// ========================
export const BORU_TEMPLATES: SkillTemplate[] = [
  {
    name: 'Kurt Pençesi',
    nameKey: 'skill_template.boru.claw.name',
    description: 'Hızlı pençe saldırısı.',
    descriptionKey: 'skill_template.boru.claw.description',
    baseType: 'slash',
    sliders: { damage: 40, area: 50, speed: 70, projectiles: 50, range: 50 },
  },
  {
    name: 'Uluyan Rüzgar',
    nameKey: 'skill_template.boru.howl.name',
    description: 'Etraftaki düşmanları geri iten uluma.',
    descriptionKey: 'skill_template.boru.howl.description',
    baseType: 'spin',
    sliders: { damage: 50, area: 60, speed: 50, projectiles: 50, range: 50 },
  },
  {
    name: 'Vahşi Sıçrayış',
    nameKey: 'skill_template.boru.leap.name',
    description: 'Hedefe doğru vahşice sıçrayarak saldırı.',
    descriptionKey: 'skill_template.boru.leap.description',
    baseType: 'slam',
    sliders: { damage: 60, area: 50, speed: 50, projectiles: 50, range: 70 },
  },
];

// Get templates by class
export function getTemplatesByClass(className: string): SkillTemplate[] {
  switch (className.toLowerCase()) {
    case 'alp':
      return ALP_TEMPLATES;
    case 'kam':
      return KAM_TEMPLATES;
    case 'mergen':
      return MERGEN_TEMPLATES;
    case 'boru':
      return BORU_TEMPLATES;
    default:
      return [];
  }
}

// Create a skill from template
export function createSkillFromTemplate(template: SkillTemplate): CustomSkill {
  const skill = SkillCreator.createSkill(template.baseType, template.name);

  // Apply template sliders
  skill.sliders = { ...template.sliders };

  // Recalculate costs
  SkillCreator.recalculateCosts(skill);

  return skill;
}

// Initialize starter skills for a class
export function initializeStarterSkills(className: string): CustomSkill[] {
  const templates = getTemplatesByClass(className);
  return templates.map(t => createSkillFromTemplate(t));
}

// ========================
// Starter Effect Stones
// ========================

// Create a unique ID for effect stones
function generateStoneId(): string {
  return `stone_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

// All starter effect stones
export function initializeStarterEffectStones(): EffectStone[] {
  const stones: EffectStone[] = [];

  // Od (Fire) Stones
  stones.push({
    id: generateStoneId(),
    name: 'Od Taşı - Yanık',
    nameKey: 'stone.od.burn',
    type: 'od',
    rarity: 'normal',
    effect: {
      id: 'od_burn',
      name: 'Yanık',
      description: 'Hasar bonusu: +%5',
      damageBonus: 5,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Od Taşı - Alev Özü',
    nameKey: 'stone.od.fire_essence',
    type: 'od',
    rarity: 'magic',
    effect: {
      id: 'od_essence',
      name: 'Alev Özü',
      description: 'Hasarı Od elementine dönüştürür, %8 hasar bonusu',
      elementOverride: 'od' as ElementType,
      damageBonus: 8,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Od Taşı - Cehennem Ateşi',
    nameKey: 'stone.od.hellfire',
    type: 'od',
    rarity: 'rare',
    effect: {
      id: 'od_hellfire',
      name: 'Cehennem Ateşi',
      description: 'Düşmanları yakar, saniyede 5 hasar, 3 saniye, %12 hasar',
      damageBonus: 12,
      dot: { damage: 5, duration: 3 },
    },
  });

  // Gök (Lightning) Stones
  stones.push({
    id: generateStoneId(),
    name: 'Gök Taşı - Şimşek',
    nameKey: 'stone.gok.lightning',
    type: 'gok',
    rarity: 'normal',
    effect: {
      id: 'gok_lightning',
      name: 'Şimşek',
      description: 'Bekleme süresini %5 azaltır',
      cooldownReduction: 5,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Gök Taşı - Zincir',
    nameKey: 'stone.gok.chain',
    type: 'gok',
    rarity: 'magic',
    effect: {
      id: 'gok_chain',
      name: 'Zincir',
      description: 'Saldırı 2 ek düşmana zincirler',
      chainCount: 2,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Gök Taşı - Fırtına Gücü',
    nameKey: 'stone.gok.storm',
    type: 'gok',
    rarity: 'rare',
    effect: {
      id: 'gok_storm',
      name: 'Fırtına Gücü',
      description: 'Hasarı Gök elementine dönüştürür, %12 hasar bonusu, 3 zincir',
      elementOverride: 'gok' as ElementType,
      damageBonus: 12,
      chainCount: 3,
    },
  });

  // Su (Water/Ice) Stones
  stones.push({
    id: generateStoneId(),
    name: 'Su Taşı - Soğuk',
    nameKey: 'stone.su.cold',
    type: 'su',
    rarity: 'normal',
    effect: {
      id: 'su_cold',
      name: 'Soğuk',
      description: 'Düşmanları %5 yavaşlatır',
      slow: 5,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Su Taşı - Dondurucu',
    nameKey: 'stone.su.freeze',
    type: 'su',
    rarity: 'magic',
    effect: {
      id: 'su_freeze',
      name: 'Dondurucu',
      description: 'Düşmanları %8 yavaşlatır, mana maliyetini %6 azaltır',
      slow: 8,
      manaCostReduction: 6,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Su Taşı - Buzul Özü',
    nameKey: 'stone.su.glacier',
    type: 'su',
    rarity: 'rare',
    effect: {
      id: 'su_glacier',
      name: 'Buzul Özü',
      description: 'Hasarı Su elementine dönüştürür, %15 yavaşlatma, %10 hasar',
      elementOverride: 'su' as ElementType,
      slow: 15,
      damageBonus: 10,
    },
  });

  // Yer (Earth) Stones
  stones.push({
    id: generateStoneId(),
    name: 'Yer Taşı - Kaya',
    nameKey: 'stone.yer.rock',
    type: 'karanlik',
    rarity: 'normal',
    effect: {
      id: 'yer_rock',
      name: 'Kaya',
      description: 'Etki alanını %5 artırır',
      areaBonus: 5,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Yer Taşı - Deprem',
    nameKey: 'stone.yer.quake',
    type: 'karanlik',
    rarity: 'magic',
    effect: {
      id: 'yer_quake',
      name: 'Deprem',
      description: 'Düşmanları geri iter, etki alanı %8 artar',
      knockback: 15,
      areaBonus: 8,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Yer Taşı - Dağ Gücü',
    nameKey: 'stone.yer.mountain',
    type: 'karanlik',
    rarity: 'rare',
    effect: {
      id: 'yer_mountain',
      name: 'Dağ Gücü',
      description: 'Hasarı Yer elementine dönüştürür, %12 hasar, geri itme',
      elementOverride: 'karanlik' as ElementType,
      damageBonus: 12,
      knockback: 25,
      areaBonus: 10,
    },
  });

  // Kut (Spirit/Holy) Stones
  stones.push({
    id: generateStoneId(),
    name: 'Kut Taşı - Ruh',
    nameKey: 'stone.kut.spirit',
    type: 'karanlik',
    rarity: 'normal',
    effect: {
      id: 'kut_spirit',
      name: 'Ruh',
      description: 'Hasarın %3\'ünü can olarak çalar',
      lifesteal: 3,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Kut Taşı - Ruh Emici',
    nameKey: 'stone.kut.drain',
    type: 'karanlik',
    rarity: 'magic',
    effect: {
      id: 'kut_drain',
      name: 'Ruh Emici',
      description: 'Hasarın %7\'sini can olarak çalar, mana maliyeti %6 azalır',
      lifesteal: 7,
      manaCostReduction: 6,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Kut Taşı - Kutsal Güç',
    nameKey: 'stone.kut.holy',
    type: 'karanlik',
    rarity: 'rare',
    effect: {
      id: 'kut_holy',
      name: 'Kutsal Güç',
      description: 'Hasarı Kut elementine dönüştürür, %10 can çalma, %12 hasar',
      elementOverride: 'karanlik' as ElementType,
      lifesteal: 10,
      damageBonus: 12,
    },
  });

  // Legendary Stones (one per element)
  stones.push({
    id: generateStoneId(),
    name: 'Efsanevi Od Taşı - Anka\'nın Kalbi',
    nameKey: 'stone.od.legendary',
    type: 'od',
    rarity: 'legendary',
    effect: {
      id: 'od_legendary',
      name: 'Anka\'nın Kalbi',
      description: 'Od elementine dönüştürür, %18 hasar, güçlü yanık efekti, %10 bekleme',
      elementOverride: 'od' as ElementType,
      damageBonus: 18,
      dot: { damage: 8, duration: 4 },
      cooldownReduction: 10,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Efsanevi Gök Taşı - Gök Tengri\'nin Gazabı',
    nameKey: 'stone.gok.legendary',
    type: 'gok',
    rarity: 'legendary',
    effect: {
      id: 'gok_legendary',
      name: 'Gök Tengri\'nin Gazabı',
      description: 'Gök elementine dönüştürür, %18 hasar, 4 zincir, %15 bekleme',
      elementOverride: 'gok' as ElementType,
      damageBonus: 18,
      chainCount: 4,
      cooldownReduction: 15,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Efsanevi Su Taşı - Sonsuz Kış',
    nameKey: 'stone.su.legendary',
    type: 'su',
    rarity: 'legendary',
    effect: {
      id: 'su_legendary',
      name: 'Sonsuz Kış',
      description: 'Su elementine dönüştürür, %20 yavaşlatma, %15 hasar, %12 mana',
      elementOverride: 'su' as ElementType,
      slow: 20,
      damageBonus: 15,
      manaCostReduction: 12,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Efsanevi Yer Taşı - Dünya Ana\'nın Gücü',
    nameKey: 'stone.yer.legendary',
    type: 'karanlik',
    rarity: 'legendary',
    effect: {
      id: 'yer_legendary',
      name: 'Dünya Ana\'nın Gücü',
      description: 'Yer elementine dönüştürür, %25 alan, %18 hasar, güçlü geri itme',
      elementOverride: 'karanlik' as ElementType,
      areaBonus: 25,
      damageBonus: 18,
      knockback: 35,
    },
  });

  stones.push({
    id: generateStoneId(),
    name: 'Efsanevi Kut Taşı - Ruhların Efendisi',
    nameKey: 'stone.kut.legendary',
    type: 'karanlik',
    rarity: 'legendary',
    effect: {
      id: 'kut_legendary',
      name: 'Ruhların Efendisi',
      description: 'Kut elementine dönüştürür, %12 can çalma, %18 hasar, %15 mana',
      elementOverride: 'karanlik' as ElementType,
      lifesteal: 12,
      damageBonus: 18,
      manaCostReduction: 15,
    },
  });

  return stones;
}
